import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { conferenceAPI } from '../services/api';
import { ChevronLeft, ChevronRight, Loader2, Calendar as CalendarIcon } from 'lucide-react';

const FullCalendar = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);

  useEffect(() => {
    fetchConferences();
  }, []);

  useEffect(() => {
    generateCalendar();
  }, [currentDate, conferences]);

  const fetchConferences = async () => {
    try {
      const response = await conferenceAPI.getAll({});
      setConferences(response.data);
    } catch (err) {
      console.error('Error fetching conferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    
    // Add empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayConferences = conferences.filter(conf => {
        const confDate = new Date(conf.date).toISOString().split('T')[0];
        return confDate === dateStr;
      });
      
      days.push({
        day,
        date,
        conferences: dayConferences
      });
    }
    
    setCalendarDays(days);
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Header />
        <div className="container mx-auto px-4 py-16 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Full Calendar
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-12">
            Browse all published conferences by date.
          </p>

          {/* Calendar Navigation */}
          <Card className="p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="outline"
                onClick={previousMonth}
                className="flex items-center"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Prev
              </Button>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {monthName}
              </h2>
              <Button
                variant="outline"
                onClick={nextMonth}
                className="flex items-center"
              >
                Next
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  className="text-center font-semibold text-gray-600 dark:text-gray-400 py-2"
                >
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {calendarDays.map((dayData, idx) => (
                <div
                  key={idx}
                  className={`min-h-24 border border-gray-200 dark:border-gray-700 rounded-lg p-2 ${
                    dayData ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'
                  }`}
                >
                  {dayData && (
                    <>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                        {dayData.day}
                      </div>
                      {dayData.conferences.length > 0 && (
                        <div className="space-y-1">
                          {dayData.conferences.map((conf) => (
                            <Link
                              key={conf.id}
                              to={`/conferences/${conf.slug || conf.id}`}
                              className="block text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-1 rounded hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
                            >
                              {conf.title.length > 30 ? conf.title.substring(0, 30) + '...' : conf.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* All Events List */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              All events ({conferences.length}) • newest first
            </h2>
            
            <div className="space-y-4">
              {conferences
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map((conf) => (
                  <Link
                    key={conf.id}
                    to={`/conferences/${conf.id}`}
                    className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md hover:border-purple-300 dark:hover:border-purple-600 transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          {conf.title}
                        </h3>
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 gap-4">
                          <span className="flex items-center">
                            <CalendarIcon className="w-4 h-4 mr-1" />
                            {formatDate(conf.date)}
                          </span>
                          <span>• {conf.location}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {conf.categories.slice(0, 2).map((cat, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded"
                          >
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default FullCalendar;
